<template>
  <div class="creator-search-page">
    <section class="creator-search-panel">
      <div class="creator-search-primary">
        <v-text-field
          v-model="filters.keyword"
          prepend-inner-icon="mdi-magnify"
          outlined
          hide-details
          label="Search creators"
          placeholder="Name, handle, email, keyword or category"
          class="creator-search-input"
          dense
          @keyup.enter="paginate()">
        </v-text-field>
        <v-btn @click="paginate()" color="secondary" class="creator-search-submit">
          <v-icon left small>mdi-magnify</v-icon>Search
        </v-btn>
        <v-btn outlined class="creator-filters-toggle" @click="filtersExpanded = !filtersExpanded">
          <v-icon left small>mdi-tune-variant</v-icon>
          Filters
          <span v-if="activeFilterCount" class="creator-filter-count">{{ activeFilterCount }}</span>
          <v-icon right small>{{ filtersExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
        </v-btn>
        <v-menu offset-y left>
          <template v-slot:activator="{ on, attrs }">
            <v-btn outlined class="creator-actions-toggle" v-bind="attrs" v-on="on">
              <v-icon left small>mdi-dots-horizontal</v-icon>Actions
            </v-btn>
          </template>
          <v-list dense class="creator-actions-menu">
            <v-list-item @click.stop="reload()">
              <v-list-item-icon><v-icon small>mdi-refresh</v-icon></v-list-item-icon>
              <v-list-item-title>Reset filters</v-list-item-title>
            </v-list-item>
            <v-list-item @click="downloadSampleUpload">
              <v-list-item-icon><v-icon small>mdi-download</v-icon></v-list-item-icon>
              <v-list-item-title>Download template</v-list-item-title>
            </v-list-item>
            <v-list-item @click="$refs.igBulkHandle.click()" v-if="isAdminRole">
              <v-list-item-icon><v-icon small>mdi-home-import-outline</v-icon></v-list-item-icon>
              <v-list-item-title>Import creators</v-list-item-title>
            </v-list-item>
            <v-list-item @click="exportExcel" v-if="isAdminRole">
              <v-list-item-icon><v-icon small>mdi-file-excel-outline</v-icon></v-list-item-icon>
              <v-list-item-title>Export results</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>

      <v-expand-transition>
        <div v-show="filtersExpanded" class="creator-filters-accordion">
      <div class="creator-quick-filters">
        <span class="creator-quick-filters__label">Quick filters</span>
        <div class="creator-quick-filters__options">
            <div class="creator-filter-toggle">
              <v-checkbox
                class="mt-0"
                v-model="filters.imsOnly"
                label="IMS Onboarded"
                color="secondary"
                :value="false"
                hide-details
                @change="paginate()"
              ></v-checkbox>
            </div>
            <div class="creator-filter-toggle">
              <v-checkbox
                class="mt-0"
                v-model="filters.rateCardOnly"
                label="Rate Card Submitted"
                color="secondary"
                :value="false"
                hide-details
                @change="paginate()"
              ></v-checkbox>
            </div>
            <div class="creator-filter-toggle">
              <v-checkbox
                class="mt-0"
                v-model="filters.pairedCreatorOnly"
                label="Past Campaigns"
                color="secondary"
                :value="false"
                hide-details
                @change="paginate()"
              ></v-checkbox>
            </div>
            <div class="creator-filter-toggle">
              <v-checkbox
                class="mt-0"
                v-model="filters.travelFormOnly"
                label="Travel Form"
                color="secondary"
                :value="false"
                hide-details
                @change="paginate()"
              ></v-checkbox>
            </div>
        </div>
      </div>

    <div class="creator-advanced-filters">
      <div class="creator-advanced-filters__header">
        <div>
          <span>Advanced filters</span>
          <small>Audience, campaign, travel and pricing details</small>
        </div>
        <v-icon color="#8b92a5">mdi-tune-variant</v-icon>
      </div>
    <!-- <v-divider></v-divider> -->
    <v-row class="row align-left" style="text-align: left; justify-content: left; margin: 5px 10% 0px">
      <!-- <v-col cols="12" xs="12" md="2" lg="2">
        <v-select
          outlined
          hide-details="auto"
          :items="platformLists"
          item-value="value"
          item-text="text"
          label="Platform"
          v-model="filters.platform"
        ></v-select>
      </v-col> -->
      <v-col cols="12" xs="12" md="3" style="max-width:220px">         
        <v-combobox
          v-model="filters.country"
          :items="countries"
          hide-selected
          multiple            
          clearable
          small-chips
          outlined
          dense
          hide-details="auto"
          label="Country"
          @change="paginate()"
        >
          <template v-slot:selection="data">
            <v-chip :input-value="data.selected" close @click:close="remove(data.item)">
              <strong>{{ data.item }}</strong>&nbsp;
            </v-chip>
          </template>
        </v-combobox>
      </v-col>
      <v-col cols="12" xs="12" md="2" lg="2" style="max-width:200px">
        <!-- <v-select
          outlined
          dense
          hide-details="auto"
          :items="types"
          item-value="value"
          item-text="text"
          v-model="filters.type"
          label="Type"
          @change="paginate()"
        ></v-select> -->
        <v-autocomplete
          v-model="filters.tags"
          :items="allTags"
          item-text="name"
          item-value="id"
          hide-selected
          outlined
          dense
          hide-details
          chips
          label="Tag"
          multiple
          @change="paginate()"
        >
          <template v-slot:selection="{ attrs, item, parent, selected }">
            <v-chip
              v-if="item === Object(item)"
              v-bind="attrs"
              :input-value="selected"
              label
              small
            >
              <span class="pr-2">
                {{ item.name }}
              </span>
              <v-icon
                small
                @click="parent.selectItem(item)"
              >
                $delete
              </v-icon>
            </v-chip>
          </template>
        </v-autocomplete>
      </v-col>
      <v-col cols="12" xs="12" md="2" lg="2" style="max-width:150px">
        <v-select
          outlined
          dense
          hide-details="auto"
          label="Gender"
          :items="genderOptions"
          item-value="value"
          item-text="text"
          v-model="filters.gender"
          @change="paginate()"
        ></v-select>
      </v-col>
      <v-col cols="12" xs="12" md="2" lg="2" style="max-width:160px">
        <v-select
          outlined
          dense
          hide-details="auto"
          label="Platform"
          :items="platformOptions"
          item-value="value"
          item-text="text"
          v-model="filters.platform"
          @change="paginate()"
        >
          <template v-slot:item="{ item }">
            <div class="creator-platform-option">
              <v-icon
                v-if="item.icon"
                :color="item.iconColor"
                class="creator-platform-option__icon"
                small
              >{{item.icon}}</v-icon>
              <img
                v-else-if="item.imgIcon"
                :src="item.imgIcon"
                class="creator-platform-option__image"
                alt=""
                aria-hidden="true"
              >
              <span>{{item.text}}</span>
            </div>
          </template>
          <template v-slot:selection="{ item }">
            <div class="creator-platform-option creator-platform-option--selected">
              <v-icon
                v-if="item.icon"
                :color="item.iconColor"
                class="creator-platform-option__icon"
                small
              >{{item.icon}}</v-icon>
              <img
                v-else-if="item.imgIcon"
                :src="item.imgIcon"
                class="creator-platform-option__image"
                alt=""
                aria-hidden="true"
              >
              <span>{{item.text}}</span>
            </div>
          </template>
        </v-select>
      </v-col>
      <v-col cols="12" xs="12" md="2" lg="2" style="max-width:230px">
        <v-select
          outlined
          dense
          hide-details="auto"
          label="Tier"
          :items="infTiers"
          item-value="value"
          item-text="text"
          v-model="filters.tier"
          @change="paginate()"
        ></v-select>
      </v-col>
      <!-- <v-col cols="12" xs="12" md="3">
        <v-combobox
          v-model="filters.infCategory"
          :items="infCategories"
          hide-selected
          multiple
          clearable
          small-chips
          outlined
          hide-details="auto"
          label="Genre"               
        >
          <template v-slot:selection="data">
            <v-chip :input-value="data.selected" close @click:close="removeCategory(data.item)">
              <strong>{{ data.item }}</strong>&nbsp;
            </v-chip>
          </template>
        </v-combobox>
      </v-col> -->
      <v-col cols="12" xs="12" md="3" lg="3" style="max-width:220px">
        <v-autocomplete
          :items="campaignVerticals"
          item-value="id"
          item-text="name"
          label="Campaign Verticals"
          v-model="filters.vertical"
          hide-details
          outlined
          dense
          clearable
        ></v-autocomplete>
      </v-col>
      <v-col cols="12" xs="12" md="2" lg="2" style="max-width:200px">
        <v-autocomplete
          :items="campaignTypeDisplayFilter"
          item-value="value"
          item-text="text"
          label="Type"
          v-model="filters.campaignType"
          hide-details
          outlined
          dense
          clearable
        ></v-autocomplete>
      </v-col>
      <v-col cols="12" xs="12" md="2" lg="2" style="max-width:150px">
        <v-autocomplete
          :items="ageGroupDisplayFilter"
          item-value="value"
          item-text="text"
          label="Age Group"
          v-model="filters.ageGroup"
          hide-details
          outlined
          dense
          clearable
        ></v-autocomplete>
      </v-col>
      <v-col cols="12" xs="12" md="2" lg="2" style="max-width:160px">
        <v-menu
          ref="DOBDatePicker"
          v-model="dateOfBirthCal"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          dense
          max-width="350px"
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              label="Date of Birth"
              placeholder="Date of Birth"
              v-model="filters.dateOfBirth"
              dense
              readonly
              outlined
              clearable
              hide-details
              v-bind="attrs"
              v-on="on"
            ></v-text-field>
          </template>
          <v-date-picker
            v-model="filters.dateOfBirth"
            type="month"
            no-title
            scrollable
          >
            <v-spacer></v-spacer>
            <v-btn
                text
                color="secondary"
                @click="cancelDOB()"
            >
                Cancel
            </v-btn>
            <v-btn
                text
                color="secondary"
                @click="okDOB()"
            >
                OK
            </v-btn>
          </v-date-picker>
        </v-menu>
      </v-col>
      <v-col cols="12" xs="12" md="2" lg="2" style="max-width:230px">
        <v-text-field
          type="number"
          v-model="filters.age"
          dense
          hide-details
          outlined
          clearable
          placeholder="Age"
          v-bind="attrs"
          v-on="on"
        ></v-text-field>
      </v-col>
      <v-col cols="12" xs="12" md="3" style="max-width:220px">
        <v-text-field
          v-model="filters.travelCountry"
          dense
          hide-details
          outlined
          clearable
          placeholder="Travel Destinations"
          v-bind="attrs"
          v-on="on"
        ></v-text-field>        
        <!-- <v-combobox
          v-model="filters.travelCountry"
          :items="countries"
          hide-selected  
          clearable
          small-chips
          outlined
          dense
          hide-details="auto"
          label="Travel Destinations"
          @change="paginate()"
        >
          <template v-slot:selection="data">
            <v-chip :input-value="data.selected">
              <strong>{{ data.item }}</strong>&nbsp;
            </v-chip>
          </template>
          <template v-slot:selection="data">
            <v-chip :input-value="data.selected" close @click:close="removeTravelCountry(data.item)">
              <strong>{{ data.item }}</strong>&nbsp;
            </v-chip>
          </template>
        </v-combobox> -->
      </v-col>
      <v-col cols="12" xs="12" md="2" lg="2" style="max-width:200px">
        <v-menu
          ref="DOBDatePicker"
          v-model="travelDateCal"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          dense
          max-width="350px"
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              label="Travel Period"
              placeholder="Travel Period"
              v-model="filters.travelDate"
              dense
              readonly
              outlined
              clearable
              hide-details
              v-bind="attrs"
              v-on="on"
            ></v-text-field>
          </template>
          <v-date-picker
            v-model="filters.travelDate"
            type="month"
            no-title
            scrollable
          >
            <v-spacer></v-spacer>
            <v-btn
                text
                color="secondary"
                @click="cancelTravelDate()"
            >
                Cancel
            </v-btn>
            <v-btn
                text
                color="secondary"
                @click="okTravelDate()"
            >
                OK
            </v-btn>
          </v-date-picker>
        </v-menu>
      </v-col>
      <v-col cols="12" xs="12" md="4" lg="4" style="max-width:310px">
        <v-autocomplete
          dense
          outlined
          clearable
          :items="allCampaigns"
          label="Campaign"
          item-value="name"
          hide-details
          item-text="fullName"
          v-model="filters.campaign"
          @change="paginate()"
        >
          <template v-slot:item="{ item }">
            <div>
              {{item.fullName}}
            </div>
          </template>
        </v-autocomplete>
      </v-col>
      <v-col cols="12" xs="12" md="3" lg="3" style="max-width:160px">
        <v-menu
          v-model="rateFilterMenu"
          offset-y
          bottom
          transition="scale-transition"
          :close-on-content-click="false"
          min-width="300px"
          max-width="300px"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-bind="attrs"
              v-on="on"
              :value="rateFilterLabel"
              label="Rate"
              append-icon="mdi-menu-down"
              outlined
              dense
              readonly
              clearable
              hide-details="auto"
              class="creator-rate-filter"
              @click:clear.stop="clearRateFilter"
            ></v-text-field>
          </template>

          <v-list min-height="100px">
            <v-list-item class="mb-2">
              <v-row>
                <v-col cols="12" xs="12" md="12" lg="12">
                  Content Type
                  <v-select
                    outlined
                    dense        
                    clearable
                    color="secondary"
                    hide-details="auto"
                    :items="contentTypes"
                    item-text="title"
                    item-value="value"
                    v-model="filters.contentType"
                    class="creator-rate-popover-input"
                    style="max-width:100%;"
                  >
                    <template v-slot:item="{ item }">
                      <div
                        class="creator-content-type-option"
                        :style="{ '--media-icon-color': item.iconColor || '#616161' }"
                      >
                        <v-icon v-if="item.icon" small>{{ item.icon }}</v-icon>
                        <img v-else-if="item.imgIcon" :src="item.imgIcon" alt="" aria-hidden="true">
                        <span>{{ item.title }}</span>
                      </div>
                    </template>
                    <template v-slot:selection="{ item }">
                      <div
                        class="creator-content-type-option creator-content-type-option--selected"
                        :style="{ '--media-icon-color': item.iconColor || '#616161' }"
                      >
                        <v-icon v-if="item.icon" small>{{ item.icon }}</v-icon>
                        <img v-else-if="item.imgIcon" :src="item.imgIcon" alt="" aria-hidden="true">
                        <span>{{ item.title }}</span>
                      </div>
                    </template>
                  </v-select>
                </v-col>
              </v-row>
            </v-list-item>
            <v-list-item class="mb-2">
              <v-row>
                <v-col cols="12" xs="12" md="12" lg="12">
                  Currency
                  <v-combobox
                    outlined
                    dense        
                    clearable
                    color="secondary"
                    hide-details="auto"
                    :items="currencyOptions"
                    v-model="filters.rateCurrency"
                    class="creator-rate-popover-input"
                    style="max-width:100%;"
                  ></v-combobox>
                </v-col>
              </v-row>
            </v-list-item>
            <v-list-item class="mb-2">
              <v-row>
                <v-col cols="12" xs="12" md="6" lg="6">
                  From
                  <v-text-field
                    outlined
                    dense        
                    clearable
                    color="secondary"
                    type="number"
                    hide-details="auto"
                    v-model="filters.rate.from"
                    class="creator-rate-popover-input"
                    style="max-width:120px;" 
                  ></v-text-field>
                </v-col>
                <v-col cols="12" xs="12" md="6" lg="6">
                  To
                  <v-text-field
                    outlined
                    dense        
                    clearable
                    color="secondary"
                    type="number"
                    hide-details="auto"
                    v-model="filters.rate.to"
                    class="creator-rate-popover-input"
                    style="max-width:120px;" 
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-list-item>
          </v-list>
          
          <v-list min-height="100px" style="min-height:60px;">
            <v-list-item class="creator-rate-filter-actions">
              <v-btn text small @click.stop="clearRateFilter()">
                Clear all
              </v-btn>
              <v-btn color="secondary" small @click.stop="applyRateFilter()">
                Apply
              </v-btn>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-col>
      <!-- <v-col cols="12" xs="12" md="2" lg="2" style="max-width:200px">
        <v-checkbox
          class="mt-0"
          v-model="filters.travelFormOnly"
          label="Travel Form"
          color="secondary"
          :value="false"
          hide-details
          @change="paginate()"
        ></v-checkbox>
      </v-col> -->
    </v-row>
    </div>
        </div>
      </v-expand-transition>
    </section>

    <section class="creator-results-panel">
      <div class="creator-results-toolbar">
        <div>
          <span v-if="selectedLists.length === 0" class="creator-results-count">
            <strong>{{ totalItems || 0 }}</strong> creators
          </span>
          <!-- <v-checkbox
            class="filter-checkbox mt-1"
            style="max-width:150px;"
            v-model="filters.validityCheck"
            label="Account Validity"
            color="secondary"
            :value="false"
            hide-details
            @change="setAccountValidity()"
          ></v-checkbox> -->
          <v-btn
            small
            color="red"
            :disabled="!selectedLists || selectedLists.length === 0"
            @click="deleteMultipleInf()"
            v-show="selectedLists.length > 0 && isSuperAdmin"
          >
          <v-icon small>mdi-delete-forever</v-icon> Delete ({{selectedLists.length}})</v-btn>
        </div>
        <div class="text-right creator-results-toolbar__actions">
          <v-menu
            bottom
            transition="scale-transition"
            :close-on-content-click="false"
            style="margin-left:-25px;"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                color="secondary"
                v-bind="attrs"
                v-on="on"
                text
              >
                <v-icon>mdi-sort</v-icon>
              </v-btn>
            </template>
            <v-list style="max-width:230px" min-height="100px">
              <v-list-item>
                <v-row>
                  <v-col cols="12" xs="12" md="12" lg="12">
                    Sort By
                    <v-select
                      outlined
                      dense
                      clearable
                      hide-details="auto"
                      :items="sortColumns"
                      v-model="sort.column"
                      item-text="text"
                      item-value="value"
                      style="max-width:200px;" 
                    ></v-select>
                  </v-col>
                  <v-col cols="12" xs="12" md="12" lg="12">
                    Type
                    <v-select
                      outlined
                      dense
                      hide-details="auto"
                      :items="sortTypes"
                      v-model="sort.type"
                      style="max-width:200px;" 
                    ></v-select>
                  </v-col>
                </v-row>
              </v-list-item>
            </v-list>
            <v-list min-height="100px" style="min-height:60px;margin-top:-20px">
              <v-list-item style="float:right">
                <v-btn color="secondary" small @click.stop="paginate()">
                  Apply
                </v-btn>
              </v-list-item>
            </v-list>
          </v-menu>
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                color="secondary"
                v-bind="attrs"
                v-on="on"
                @click="openColSettingsModal"
              >
                <i class="fas fa-cog fa-lg"></i>
              </v-btn>
            </template>
            <span>Column settings</span>
          </v-tooltip>
        </div>
      </div>
      <v-data-table
        id="search-creator-table"
        :headers="computedHeaders"
        :items="filteredItems"
        :loading="loading"
        :options.sync="pagination"
        :server-items-length="totalItems"
        :footer-props="{
          itemsPerPageOptions: rowsPerPageItems
        }"
        :items-per-page="20"
        show-select
        v-model="selectedLists"
        @toggle-select-all="selectAllLists"
        item-key="_id"
        class="elevation-1"
      >
        <template v-slot:[`item.${customSlots.instagram}`]="{ item }">
          <tr>
            <td class="text-xs-left" style="min-width:150px">
              <div v-if="item['instagram']">
                <social-handle
                  :isInstagram="true"
                  :isTikTok="false"
                  :isYoutube="false"
                  :instagramHandle="item['instagram']"
                  divClassName="float-left"
                />
                <v-tooltip v-if="item.igCheck" bottom>
                  <template v-slot:activator="{ on }">
                    <v-icon @click="true" color="green" v-on="on" small>done</v-icon>
                  </template>
                  <span>Registered on IMS</span>
                </v-tooltip>
                <v-tooltip v-if="item.igCheck === false" bottom>
                  <template v-slot:activator="{ on }">
                    <v-icon @click="true" color="red" v-on="on" small>cancel</v-icon>
                  </template>
                  <span>Not in IMS</span>
                </v-tooltip>
              </div>
            </td>
          </tr>
        </template>
        <template v-slot:[`item.${customSlots.tiktok}`]="{ item }">
          <tr>
            <td class="text-xs-left" style="min-width:150px">
              <div v-if="item['tiktok']">
                <social-handle
                  :isInstagram="false"
                  :isTikTok="true"
                  :isYoutube="false"
                  :tiktokHandle="item['tiktok']"
                  divClassName="float-left"
                />
                <v-tooltip v-if="item.tiktokCheck" bottom>
                  <template v-slot:activator="{ on }">
                    <v-icon @click="true" color="green" v-on="on">done</v-icon>
                  </template>
                  <span>Registered on IMS</span>
                </v-tooltip>
                <v-tooltip v-if="item.tiktokCheck === false" bottom>
                  <template v-slot:activator="{ on }">
                    <v-icon @click="true" color="red" v-on="on">cancel</v-icon>
                  </template>
                  <span>Not in IMS</span>
                </v-tooltip>
              </div>
            </td>
          </tr>
        </template>
        <template v-slot:[`item.${customSlots.youtube}`]="{ item }">
          <tr>
            <td class="text-xs-left" style="min-width:150px">
              <div v-if="item['youtube']">
                <social-handle
                  :isInstagram="false"
                  :isTikTok="false"
                  :isYoutube="true"
                  :youtubeHandle="item['youtube']"
                  divClassName="float-left"
                />
                <v-tooltip v-if="item.youtubeCheck" bottom>
                  <template v-slot:activator="{ on }">
                    <v-icon @click="true" color="green" v-on="on">done</v-icon>
                  </template>
                  <span>Registered on IMS</span>
                </v-tooltip>
                <v-tooltip v-if="item.youtubeCheck === false" bottom>
                  <template v-slot:activator="{ on }">
                    <v-icon @click="true" color="red" v-on="on">cancel</v-icon>
                  </template>
                  <span>Not in IMS</span>
                </v-tooltip>
              </div>
            </td>
          </tr>
        </template>
        <template v-slot:[`item.${customSlots.linkedin}`]="{ item }">
          <tr>
            <td class="text-xs-left" style="min-width:150px">
              <div v-if="getLinkedInHandle(item)">
                <social-handle
                  :isLinkedIn="true"
                  :linkedinHandle="getLinkedInHandle(item)"
                  divClassName="float-left"
                />
              </div>
            </td>
          </tr>
        </template>
        <template v-slot:[`item.${customSlots.twitter}`]="{ item }">
          <tr>
            <td class="text-xs-left" style="min-width:150px">
              <div v-if="getTwitterHandle(item)">
                <social-handle
                  :isTwitter="true"
                  :twitterHandle="getTwitterHandle(item)"
                  divClassName="float-left"
                />
              </div>
            </td>
          </tr>
        </template>
        <template v-slot:[`item.xiaohongshu`]="{ item }">
          <tr>
            <td class="text-xs-left" style="min-width:150px">
              <div v-if="item['xiaohongshu']">
                <social-handle
                  :isInstagram="false"
                  :isTikTok="false"
                  :isYoutube="false"
                  :isXiaohongshu="true"
                  :xiaohongshuHandle="item['xiaohongshu']"
                  :xiaohongshuFullName="item['xhsFullName']"
                  divClassName="float-left"
                />
              </div>
            </td>
          </tr>
        </template>
        <template v-slot:[`item.${customSlots.type}`]="{ item }">
          <tr>
            <td class="text-xs-left">
              {{getTypeText(item.type)}}
            </td>
          </tr>
        </template>
        <template v-slot:[`item.pastCampaigns`]="{ item }">
          <tr>
            <td class="text-center">
              <v-chip
                class="ma-2"
                style="font-size:13px"
                color="#CFD8DC"
                text-color="black"
                small
                v-if="item.campaignsCount"
                @click="openCampaignListsModal(item, false)"
              ><span style="font-size: 11px;">Campaigns &nbsp;<strong>({{item.campaignsCount}})</strong></span></v-chip>
              <div v-if="!item.campaignsCount && item.pastCampaigns">
                <div v-for="(preferred, index) in item.pastCampaigns" :key="index">
                  <v-chip
                    class="ma-2"
                    x-small
                  >{{preferred}}</v-chip>
                </div>
              </div>
            </td>
          </tr>
        </template>
        <template v-slot:[`item.instagramFollowers`]="{ item }">
          <tr>
            <td class="text-xs-left" style="min-width:150px" v-if="item['instagramFollowers']">
              {{item['instagramFollowers'] | thousandSeprator}}
            </td>
          </tr>
        </template>
        <template v-slot:[`item.tiktokFollowers`]="{ item }">
          <tr>
            <td class="text-xs-left" style="min-width:150px" v-if="item['tiktokFollowers']">
              {{item['tiktokFollowers'] | thousandSeprator}}
            </td>
          </tr>
        </template>
        <template v-slot:[`item.youtubeFollowers`]="{ item }">
          <tr>
            <td class="text-xs-left" style="min-width:150px" v-if="item['youtubeFollowers']">
              {{item['youtubeFollowers'] | thousandSeprator}}
            </td>
          </tr>
        </template>
        <template v-slot:[`item.linkedinFollowers`]="{ item }">
          <tr>
            <td class="text-xs-left" style="min-width:170px">
              <span v-if="getLinkedInFollowers(item) !== null">{{getLinkedInFollowers(item) | thousandSeprator}}</span>
              <span v-else>-</span>
            </td>
          </tr>
        </template>
        <template v-slot:[`item.twitterFollowers`]="{ item }">
          <tr>
            <td class="text-xs-left" style="min-width:170px">
              <span v-if="getTwitterFollowers(item) !== null">{{getTwitterFollowers(item) | thousandSeprator}}</span>
              <span v-else>-</span>
            </td>
          </tr>
        </template>
        <template v-slot:[`item.xhsFollowers`]="{ item }">
          <tr>
            <td class="text-xs-left" style="min-width:150px" v-if="item['xhsFollowers']">
              {{item['xhsFollowers'] | thousandSeprator}}
            </td>
          </tr>
        </template>
        <template v-slot:[`item.email`]="{ item }">
          <td class="text-right pa-2">
            <div v-if="item.emailEdit">
              <v-text-field
                style="max-width:320px"
                v-model="item.email"
                dense
                hide-details
              ></v-text-field>
              <div class="d-flex flex-row justify-right">
                <v-btn color="success btn-edit-mth mr-1" style="margin-top:7px;" x-small @click="updateImportedCreator(item, 'email')">
                  <v-icon class="cursor-pointer" title="Close" x-small>check</v-icon>
                </v-btn>
                <v-btn color="red btn-edit-mth" style="margin-top:7px;" x-small @click="changeImportedCreatorEdit('emailEdit', item, false)">
                  <v-icon class="cursor-pointer" color="white" title="Close" x-small>close</v-icon>
                </v-btn>
              </div>
            </div>
            <div v-if="!item.emailEdit">
              <v-chip
                style="min-width:20px"
                small
                color="default"
                v-if="item.email"
              >
                {{item.email}}
              </v-chip>
              <v-btn color="secondary" icon small text @click="changeImportedCreatorEdit('emailEdit', item, true)">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }"> 
                    <v-icon class="cursor-pointer" color="secondary" title="Update" v-on="on" small>edit</v-icon>
                  </template>
                  <span>Edit Email</span>
                </v-tooltip>
              </v-btn>
            </div>
          </td>
        </template>
        <template v-slot:[`item.dateOfBirth`]="{ item }">
          <td class="text-left" style="min-width:120px!important">
            {{item.dateOfBirth ? utils.formatCustomDate(item.dateOfBirth, 'DD MMM, YYYY') : ''}}
          </td>
        </template>
        <template v-slot:[`item.ageGroup`]="{ item }">
          <td class="text-left" style="min-width:120px!important">
            {{item.ageGroup || ''}}
          </td>
        </template>
        <template v-slot:[`item.shortDescription`]="{ item }">
          <tr>
            <td class="text-xs-left" style="min-width:200px">
              <p class="remark-text" style="margin-bottom:0">{{item.shortDescription || '-'}}</p>
              <v-menu                                                            
                v-show="item.shortDescription"      
                :close-on-content-click="false"    
                top                                                 
                offset-x                              
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    :id="'btnMore_'+item.id"
                    v-show="item.shortDescription && item.shortDescription.length > 1"                       
                    color="secondary"
                    x-small
                    dark
                    v-bind="attrs"
                    v-on="on"
                    text
                  >
                    More...
                  </v-btn>
                </template>
                <v-card max-width="300" style="text-align: left;">
                  <v-card-text>
                    <div class="font-weight-bold">Short Description</div>
                    <v-divider></v-divider>                                    
                    {{item.shortDescription}}
                  </v-card-text>
                </v-card>
              </v-menu>
              <!-- <v-tooltip bottom color="black" max-width="300px">
                <template v-slot:activator="{ on }">
                  <div v-on="on">
                    <p class="remark-text">{{item.shortDescription || '-'}}</p>
                  </div>
                </template>
                <span v-html="formatDesc(item.shortDescription)"></span>
              </v-tooltip> -->
            </td>
          </tr>
        </template>
        <template v-slot:[`item.ccRemarks`]="{ item }">
          <tr>
            <!-- <td class="text-xs-left" style="min-width:200px">
              <v-tooltip bottom color="black" max-width="300px">
                <template v-slot:activator="{ on }">
                  <div v-on="on">
                    <p class="remark-text">{{item.ccRemarks || '-'}}</p>
                  </div>
                </template>
                <span v-html="formatDesc(item.ccRemarks)"></span>
              </v-tooltip>
            </td> -->
            <td class="text-center">
              <div v-if="item.ccRemarksCount">
                <v-chip
                  class="ma-2"
                  style="font-size:13px"
                  color="#CFD8DC"
                  text-color="black"
                  small
                  v-if="item.ccRemarksCount"
                  @click="openCampaignListsModal(item, true)"
                ><span style="font-size: 11px;"><strong>Remarks ({{item.ccRemarksCount}})</strong></span></v-chip>
              </div>
              <div v-if="!item.campaignsCount && item.pastCampaigns">
                <!-- <div v-for="(preferred, index) in item.pastCampaigns" :key="index">
                  <v-chip
                    class="ma-2"
                    x-small
                  >{{preferred}}</v-chip>
                </div> -->
              </div>
            </td>
          </tr>
        </template>
        <template v-slot:[`item.statsAnalysis`]="{ item }">
          <tr>
            <!-- <td class="text-xs-left" style="min-width:200px">
              <v-tooltip bottom color="black" max-width="300px">
                <template v-slot:activator="{ on }">
                  <div v-on="on">
                    <p class="remark-text">{{item.ccRemarks || '-'}}</p>
                  </div>
                </template>
                <span v-html="formatDesc(item.ccRemarks)"></span>
              </v-tooltip>
            </td> -->
            <td class="text-center">
              <v-chip
                class="ma-2"
                style="font-size:13px"
                color="#CFD8DC"
                text-color="black"
                small
                v-if="item.pairingHistory && item.pairingHistory.length"
                @click="viewStatsAnalysis(item)"
              ><span style="font-size: 11px;"><strong>View Stats</strong></span></v-chip>
            </td>
          </tr>
        </template>
        <template v-slot:[`item.travelForm`]="{ item }">
          <tr>
            <td class="text-xs-left">
              <!-- View on IMS -->
              <v-btn
                v-if="item.travelForm"
                @click="getTravelDataToView(item)"
                color="info"
                outlined
                small
              >
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on">mdi-wallet-travel</v-icon>
                  </template>
                  <span>View Travel Form</span>
                </v-tooltip>
              </v-btn>
            </td>
          </tr>
        </template>
        <template v-slot:[`item.rateCard`]="{ item }">
          <tr>
            <td class="text-xs-left">
              <v-btn
                small
                color="info"
                outlined
                @click="getRateCardDataToView(item.rateCard)"
                v-if="item.rateCardID && item.rateCard"
              >
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }"> 
                    <img src="/static/img/material_icons/mdi--money.svg" width="25px" v-on="on" />
                  </template>
                  <span>View Rate Card</span>
                </v-tooltip>
              </v-btn>
            </td>
          </tr>
        </template>
        <template v-slot:[`item.PairedCreators`]="{ item }">
          <tr>
            <td class="text-xs-left">
              <v-btn
                text
                small
                color="warning"
                :href="`/paired/creators?handle=${item.pairedCreator.handle}`"
                :target="'_blank'"
                v-if="item.pairedCreator"
              >
                View
              </v-btn>
            </td>
          </tr>
        </template>
        <template v-slot:[`item.tags`]="{ item }">
          <td>
            <span v-for="(item, index) in item.tags" :key="index">
              <v-chip
                class="ma-2"
                color="warning"
                text-color="white"
                small
              >
                {{item}}
              </v-chip>
            </span>
          </td>
        </template>
        <template v-slot:[`item.genre`]="{ item }">
          <td style="min-width:150px">
            <v-chip
              v-if="!item.genre || item.genre.length === 0"
              class="ma-2"
              color="#ffffff"
              x-small
              text-color="black"
              style="text-transform: capitalize;"
            >{{'-'}}</v-chip>
            <v-chip                                 
              v-else                              
              v-for="(keyword, index) in item.genre"
              :key="index"
              v-show="index === 0"
              class="ma-2"
              x-small             
            >{{keyword}}</v-chip> 
            <v-menu                                                            
              v-show="item.genre"      
              :close-on-content-click="false"    
              top                                                 
              offset-x                              
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  :id="'btnMore_'+item.id"
                  v-show="item.genre && item.genre.length > 1"                       
                  color="secondary"
                  x-small
                  dark
                  v-bind="attrs"
                  v-on="on"
                  text
                >
                  More...
                </v-btn>
              </template>
              <v-card max-width="300" style="text-align: left;">
                <v-card-text>
                  <div>Genre</div>
                  <v-divider></v-divider>                                    
                  <v-chip                                      
                    v-for="(interest, index) in item.genre"
                    :key="index"
                    v-show="index !== 0"
                    class="ma-2"
                    small           
                  >{{interest}}</v-chip>
                </v-card-text>
              </v-card>
            </v-menu>
          </td>
        </template>
        <template v-slot:[`item.IMS`]="{ item }">
          <tr>
            <td class="text-xs-left">
              <!-- View on IMS -->
              <v-btn
                v-if="item.influencerID"
                @click="getInfluencerDataToView(item.influencerID, item)"
                color="secondary"
                outlined
                small
              >
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on" >mdi-account-edit</v-icon>
                  </template>
                  <span>View Influencer</span>
                </v-tooltip>
              </v-btn>
            </td>
          </tr>
        </template>
        <template v-slot:[`item.Audience`]="{ item }">
          <tr>
            <td class="text-xs-left" style="min-width:180px">
              <!-- View audience data -->
              <v-btn
              class="mt-1 mb-1"
              @click="showReport(item, 'instagram')"
              color="info"
              small
              outlined
              :disabled="!item.audienceID">
                <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <v-icon v-on="on" small>assessment</v-icon>
                      <i class="fab fa-instagram" v-on="on"></i>
                  </template>
                  <span>View IG Aud</span>
                </v-tooltip>
              </v-btn>
              <!-- View tiktok audience data -->
              <v-btn
              class="mt-1 mb-1"
              @click="showReport(item, 'tiktok')"
              color="info"
              small
              outlined
              :disabled="!item.tiktokAudienceID">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on" small>assessment</v-icon>
                    <img src="/static/img/tiktok-32.png" width="17px" v-on="on" />
                  </template>
                  <span>View TikTok Aud</span>
                </v-tooltip>
              </v-btn>
              <!-- View tiktok audience data -->
              <v-btn
              class="mt-1 mb-1"
              @click="showReport(item, 'youtube')"
              color="info"
              small
              outlined
              :disabled="!item.youtubeAudienceID">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on" small>assessment</v-icon>
                    <v-icon v-on="on" small>mdi-youtube</v-icon>
                  </template>
                  <span>View Youtube Aud</span>
                </v-tooltip>
              </v-btn>
              <!-- View Youtube audience data -->
            </td>
          </tr>
        </template>
        <template v-slot:[`item.createdAt`]="{ item }">
          <td class="text-xs-left">
            {{utils.formatCustomDate(item.createdAt, 'DD-MM-YYYY') }}
          </td>
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <tr>
            <td style="min-width:280px">
              <!-- Add Tag btn -->
              <v-btn
              class="mb-1"
              @click="showAddTagDialog(item)"
              color="warning"
              small>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on" small>mdi-tag</v-icon>
                  </template>
                  <span>Add Tag</span>
                </v-tooltip>
              </v-btn>
              <!-- Fetch IQ Data -->
              <v-btn
              class="mb-1"
              @click="fetchIQData(item, 'instagram')"
              color="info"
              small
              :disabled="!item.instagram">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on" small>download</v-icon>
                    <i class="fab fa-instagram" v-on="on"></i>
                  </template>
                  <span>Fetch IG Aud</span>
                </v-tooltip>
              </v-btn>
              <!-- Fetch TikTok IQ Data -->
              <v-btn
              class="mb-1"
              @click="fetchIQData(item, 'tiktok')"
              color="info"
              small
              :disabled="!item.tiktok">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on" small>download</v-icon>
                    <img src="/static/img/tik-tok-white.png" width="12px" v-on="on" />
                  </template>
                  <span>Fetch TikTok Aud</span>
                </v-tooltip>
              </v-btn>
              <!-- Fetch Youtube IQ Data -->
              <v-btn
              class="mb-1"
              @click="fetchIQData(item, 'youtube')"
              color="info"
              small
              :disabled="!item.youtube">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on" small>download</v-icon>
                    <v-icon v-on="on" small>mdi-youtube</v-icon>
                  </template>
                  <span>Fetch Youtube Aud</span>
                </v-tooltip>
              </v-btn>
              <!-- View Details -->
              <!-- <v-btn
                class="btn-ims"
                @click="getAllInfToView(item)"
                color="warning"
                small
              >
                View Details
              </v-btn> -->
              <!-- Delete -->
              <!-- Delete btn -->
              <v-btn
                class="mb-1"
                @click="deleteInf(item)"
                color="red"
                small
                v-if="isSuperAdmin"
              >
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on" color="white" small>mdi-delete-forever</v-icon>
                  </template>
                  <span>Delete</span>
                </v-tooltip>
              </v-btn>
            </td>
          </tr>
        </template>
      </v-data-table>
    </section>
    <input
      type="file"
      accept=".xlsx"
      id="igBulkHandle"
      ref="igBulkHandle"
      style="display: none"
      @change="igHandleBulkUpload"
    />
    <!-- Audience data report modal -->
    <audience-data-report-modal
      v-model="viewAudienceDataDialog"
      :audienceData="audienceDataReport"
      @showLoadingEvent="showLoadingEvent"
    />

    <loading
      :active.sync="importLoading"
      :can-cancel="false"
      :is-full-page="true"
      loader="bars"
      background-color="#000"
      color="secondary"
      width.number="180"
      height.number="180"
    />

    <table-cols-show-hide-modal
      v-model="colSettingsDialog"
      title="Creators Search"
      :items="tableColsItems"
      @updated="updateTableCols"
    />
    <campaign-lists-modal
      :items="viewCampaigns"
      :handle="viewHandle"
      :isRemark="isCcRemarks"
      :emptyCampaigns="emptyCampaigns"
      :value="viewCampaignListsDialog"
      v-model="viewCampaignListsDialog"
    />
    <!-- Edit Report Handle Modal -->
    <add-tag-modal 
    v-model="addTagDialog"
    :item="editTagItem"
    @saved="saveTagProcess"
    />
    <!-- View Rate Card modal -->
    <ratecard-view-modal-dialog
    v-model="rateCardViewDialog"
    :value="rateCardViewDialog"
    :data="rateCardViewData"
    v-if="rateCardViewData"
    @updated="paginate"/>

    <!-- ViewInfluencer modal -->
    <view-influencer-modal v-model="viewInfluencerDialog" :influencerData="viewInfluencerData" />

    <!-- View Travel Form modal -->
    <view-travel-modal v-model="viewTravelDialog" :travelData="viewTravelData" />

    <!-- Stats Analysis Modal -->
    <view-stats-modal v-model="viewStatsDialog" :handle="viewHandle"/>
  </div>
</template>
<script>
/* eslint-disable */
import { mapState } from 'vuex';
import creatorsConfig from './config'
import config from '@/config';
import campaignConfig from '../../Campaign/config';
import rateCardConfig from '../RateCard/config';
// import influencerHelper from '../influencerHelper'
import influencerAPIs from '../influencerAPIs';
import userAPIs from '../../User/userAPIs';
import pairingAPIs from '../../Pairing/pairingAPIs';
import AudienceDataReportModal from '../AudienceDataReportModal';
import TableColsShowHideModal from '@/components/scoped/custom/TableColsShowHideModal'
import CampaignListsModal from '../../Pairing/PairedCreators/CampaignListsModal';
import RatecardViewModalDialog from '../RateCard/ViewModalDialog';
import AddTagModal from '../../Pairing/PairedCreators/AddTagModal';
import ViewStatsModal from '../../Campaign/StatisticsAnalysis/ViewModalDialog';
import ViewInfluencerModal from '../ViewInfluencerModal';
import ViewTravelModal from '../ProfileTally/ViewModalDialog';
import SocialHandle from '@/components/scoped/custom/SocialHandle';
import Loading from 'vue-loading-overlay';
import utils from '@/utils'
import countries from '../../../../../static/sources/countries';
import campaignAPIs from '../../Campaign/campaignAPIs';
import moment from 'moment';
export default {
  name: 'SearchCreators',
  components: {
    SocialHandle,
    AudienceDataReportModal,
    TableColsShowHideModal,
    CampaignListsModal,
    RatecardViewModalDialog,
    AddTagModal,
    ViewInfluencerModal,
    ViewTravelModal,
    ViewStatsModal,
    Loading
  },
  data() {
    return {
      utils: utils.func,
      platformLists: creatorsConfig.platformLists,
      types: creatorsConfig.types,
      headers: creatorsConfig.tableHeaders,
      defaultCols: creatorsConfig.defaultCols,
      infTiers: creatorsConfig.influencerTiers,
      customSlots: creatorsConfig.customSlots,
      campaignStatuses: config.campaignStatus,
      campaignStage: campaignConfig.campaignStage,
      campaignTypeDisplayFilter: campaignConfig.campaignTypeDisplay,
      ageGroupDisplayFilter: ['13-17', '18-24', '25-34', '35-44', '45-64', '65+'],
      campaignVerticals: [],
      infCategories: [],
      countries: [],
      currencyOptions: rateCardConfig.currencyOptions,
      filtersExpanded: false,
      rateFilterMenu: false,
      platformOptions: [
        {
          text: 'Any',
          value: '',
          icon: 'mdi-apps',
          iconColor: '#616161'
        },
        {
          text: 'Instagram',
          value: 'instagram',
          icon: 'mdi-instagram',
          iconColor: '#E1306C'
        },
        {
          text: 'TikTok',
          value: 'tiktok',
          imgIcon: '/static/img/tiktok.svg'
        },
        {
          text: 'Youtube',
          value: 'youtube',
          icon: 'mdi-youtube',
          iconColor: '#FF0000'
        },
        {
          text: 'LinkedIn',
          value: 'linkedin',
          icon: 'mdi-linkedin',
          iconColor: '#0A66C2'
        },
        {
          text: 'X (Twitter)',
          value: 'twitter',
          imgIcon: '/static/img/x-logo.svg'
        },
        {
          text: 'Xiaohongshu',
          value: 'xiaohongshu',
          imgIcon: '/static/img/xhs-black.png'
        }
      ],
      filters: {
        keyword: '',
        platform: '',
        tags: [],
        tagHandles: [],
        gender: '',
        tier: '',
        country: [],
        dateOfBirth: '',
        infCategory: [],
        vertical: '',
        campaignType: '',
        ageGroup: '',
        age: '',
        imsOnly: false,
        rateCardOnly: false,
        pairedCreatorOnly: false,
        travelFormOnly: false,
        travelCountry: [],
        travelDate: '',
        validityCheck: false,
        campaign: '',
        contentType: '',
        rateCurrency: '',
        rate: {from: '', to: ''},
      },
      dateOfBirthCal: false,
      travelDateCal: false,
      loading: false,
      filteredItems: [],
      selectedLists: [],
      allCampaigns: [],
      rowsPerPageItems: [10, 20, 50, 100, { text: 'All', value: -1 }],
      pagination: {
        // v-data-table pagination
        data: [],
        sortDesc: [true],
        page: 1,
        sortBy: [],
        itemsPerPage: 20
      },
      genderOptions: [
        { text: 'All', value: '' },
        { text: 'Male', value: 'Male' },
        { text: 'Female', value: 'Female' }
      ],
      sortColumns: [
        {
          text: 'IG followers',
          value: 'instagramFollowers'
        },
        {
          text: 'TikTok followers',
          value: 'tiktokFollowers'
        },
        {
          text: 'Youtube followers',
          value: 'youtubeFollowers'
        },
        {
          text: 'XHS followers',
          value: 'xhsFollowers'
        },
        {
          text: 'Past Campaigns',
          value: 'campaignsCount'
        },
        {
          text: 'Last Updated',
          value: 'updatedAt'
        }
      ],
      sortTypes: [
        'Ascending',
        'Descending'
      ],
      sort: {
        column: 'updatedAt',
        type: 'Descending'
      },
      totalItems: 0,
      shouldCallApi: false,
      importLoading: false,
      audienceDataReport: null,
      viewAudienceDataDialog: false,
      colSettingsDialog: false,
      colStatuses: {},
      userProfile: {},
      viewCampaigns: [],
      viewHandle: '',
      viewCampaignListsDialog: false,
      addTagDialog: false,
      rateCardViewData: null,
      rateCardViewDialog: false,
      editTagItem: null,
      viewInfluencerDialog: false,
      viewInfluencerData: null,
      viewTravelData: null,
      viewTravelDialog: false,
      viewStatsDialog: false,
      isCcRemarks: false,
      contentTypes: [
        { title: 'IG Story', value: 'igStory', icon: 'mdi-instagram', iconColor: '#E1306C' },
        { title: 'IG Post', value: 'igPost', icon: 'mdi-instagram', iconColor: '#E1306C' },
        { title: 'IG Carousel', value: 'igCarousel', icon: 'mdi-instagram', iconColor: '#E1306C' },
        { title: 'IG Video', value: 'igVideo', icon: 'mdi-instagram', iconColor: '#E1306C' },
        { title: 'IG Reel', value: 'igReel', icon: 'mdi-instagram', iconColor: '#E1306C' },
        { title: 'IG TV', value: 'igTv', icon: 'mdi-instagram', iconColor: '#E1306C' },
        { title: 'Loading Fee', value: 'loadingFee', icon: 'mdi-cloud-upload-outline', iconColor: '#607D8B' },
        { title: 'Event Appearance', value: 'eventAppearance', icon: 'mdi-calendar-star', iconColor: '#FF8F00' },
        { title: 'TikTok', value: 'tiktok', imgIcon: '/static/img/tiktok.svg' },
        { title: 'YouTube Post', value: 'youtube', icon: 'mdi-youtube', iconColor: '#FF0000' },
        { title: 'Facebook Post', value: 'facebook', icon: 'mdi-facebook', iconColor: '#1877F2' },
        { title: 'LinkedIn Post', value: 'linkedin', icon: 'mdi-linkedin', iconColor: '#0A66C2' },
        { title: 'X (Twitter) Post', value: 'twitter', imgIcon: '/static/img/x-logo.svg' },
        { title: 'Twitch Post', value: 'twitch', icon: 'mdi-twitch', iconColor: '#9146FF' }
      ],
    }
  },
  computed: {
    ...mapState({
      campaigns: state => state.campaign.campaigns,
      importedCreators: state => state.influencer.importedCreators,
      importedCreatorDtl: state => state.influencer.importedCreatorDtl,
      importedCreatorGenres: state => state.influencer.importedCreatorGenres,
      importedCreatorCols: state => state.influencer.importedCreatorCols,
      savedImportedCreator: state => state.influencer.savedImportedCreator,
      socialDataErrMsg: state => state.influencer.socialDataErrMsg,
      allTags: state => state.influencer.allTags,
      allAssignedTags: state => state.influencer.allAssignedTags,
      assignedTags: state => state.influencer.assignedTags
    }),
    computedHeaders() {
      let searchCreatorsCols = null
      // set col statuses from user table
      if (this.userProfile.tableSettings) {
        if (this.userProfile.tableSettings.searchCreatorsCols) {
          searchCreatorsCols = Object.assign({}, this.userProfile.tableSettings.searchCreatorsCols)
        }
      }
      this.headers.map(header => {
        if (this.defaultCols.includes(header.value)) {
          this.colStatuses[header.value] = true
        }
      })
      if (searchCreatorsCols) {
        for (var key of Object.keys(searchCreatorsCols)) {
          this.colStatuses[key] = searchCreatorsCols[key]
        }
      }
      return this.headers.filter(h => {
        if (this.colStatuses[h.value]) {
          return true
        }
        return false
      });
    },
    activeFilterCount() {
      const ignored = ['keyword', 'tagHandles', 'rate']
      let count = Object.keys(this.filters).reduce((total, key) => {
        if (ignored.includes(key)) return total
        const value = this.filters[key]
        if (Array.isArray(value)) return total + (value.length ? 1 : 0)
        return total + (value !== '' && value !== false && value !== null ? 1 : 0)
      }, 0)
      if (this.filters.rate && (this.filters.rate.from || this.filters.rate.to)) count++
      return count
    },
    rateFilterLabel() {
      const parts = []
      if (this.filters.contentType) {
        const selectedValue = typeof this.filters.contentType === 'object'
          ? this.filters.contentType.value
          : this.filters.contentType
        const contentType = this.contentTypes.find(item => item.value === selectedValue)
        const selectedLabel = typeof this.filters.contentType === 'object'
          ? (this.filters.contentType.title || this.filters.contentType.text || selectedValue)
          : selectedValue
        parts.push(contentType ? contentType.title : selectedLabel)
      }
      if (this.filters.rateCurrency) parts.push(this.filters.rateCurrency)
      if (this.filters.rate && (this.filters.rate.from || this.filters.rate.to)) {
        const from = this.filters.rate.from || 'Any'
        const to = this.filters.rate.to || 'Any'
        parts.push(`${from} - ${to}`)
      }
      return parts.join(' · ')
    },
    payload() {
      return {
        filter: this.filters,
        paging: {
          per_page: this.pagination.itemsPerPage <= 0 ? 20 : this.pagination.itemsPerPage,
          page: 1,
          sortBy: this.sort.column || '',
          sortOrder: this.sort.type === 'Ascending' ? 1 : -1
        }
      };
    },
    currentUserID: function() {
      return this.$store.state.user.id;
    },
    isAdminRole: function () {
      const role = Number(this.$store.state.user.role)
      return role === config.userRoles.SUPER_ADMIN_HIGH ||
      role === config.userRoles.SUPER_ADMIN_LOW ||
      role === config.userRoles.COMPANY_ADMIN_LOW ||
      role === config.userRoles.COMPANY_ADMIN_HIGH
    },
    isSuperAdmin: function () {
      const role = Number(this.$store.state.user.role)
      return role === config.userRoles.SUPER_ADMIN_HIGH || role === config.userRoles.SUPER_ADMIN_LOW
    }
  },
  watch: {
    pagination(newVal) {
      if (this.shouldCallApi) {
        this.paginate(newVal.page);
      }
    },
    filters: {
      handler(newVal, oldValue) {
        if (newVal.vertical || newVal.campaignType) {
          this.filters.pairedCreatorOnly = true
        }
        if (!newVal.vertical && !newVal.campaignType) {
          this.filters.pairedCreatorOnly = false
        }
      },
      deep: true
    }
  },
  methods: {
    applyRateFilter() {
      if (this.filters.contentType && typeof this.filters.contentType === 'object') {
        this.filters.contentType = this.filters.contentType.value || ''
      }
      this.rateFilterMenu = false
      this.paginate()
    },
    clearRateFilter() {
      this.filters.contentType = ''
      this.filters.rateCurrency = ''
      this.filters.rate = {from: '', to: ''}
      this.rateFilterMenu = false
      this.paginate()
    },
    getLinkedInHandle(item) {
      const linkedIn = item.linkedin && typeof item.linkedin === 'object' ? item.linkedin : {}
      return item.linkedinHandle || linkedIn.linkedinHandle || linkedIn.handle || ''
    },
    getTwitterHandle(item) {
      const twitter = item.twitter && typeof item.twitter === 'object' ? item.twitter : {}
      return item.twitterHandle || twitter.twitterHandle || twitter.handle || ''
    },
    getLinkedInFollowers(item) {
      const linkedIn = item.linkedin && typeof item.linkedin === 'object' ? item.linkedin : {}
      const value = item.linkedinFollowers !== undefined && item.linkedinFollowers !== null
        ? item.linkedinFollowers
        : linkedIn.totalFollowers
      return value !== undefined && value !== null && value !== '' ? Number(value) : null
    },
    getTwitterFollowers(item) {
      const twitter = item.twitter && typeof item.twitter === 'object' ? item.twitter : {}
      const value = item.twitterFollowers !== undefined && item.twitterFollowers !== null
        ? item.twitterFollowers
        : twitter.totalFollowers
      return value !== undefined && value !== null && value !== '' ? Number(value) : null
    },
    async paginate(page = 1) {
      // implement pagi func
      // note - pagination is currently turned off in v-data-table for searching
      this.currentPage = page;
      this.payload.paging.page = page;
      this.payload.export = false
      this.loading = true;
      // tag filter from inf and return
      this.filters.tagHandles = []
      if (this.filters.tags.length) {
        await this.$store.dispatch('influencer/getAssignedTags', {
          filters: {
            tags: this.filters.tags
          }
        })
        let handles = this.assignedTags.map(a => a.handle)
        this.filters.tagHandles = handles
        // this.filteredItems = this.filteredItems.filter(item => handles.includes(item.instagram) ||
        // handles.includes(item.tiktok) || handles.includes(item.youtube))
      }
      await this.$store.dispatch('influencer/getImportedCreators', this.payload)
      if (this.importedCreators) {
        this.beautifyData()
        this.setRemarks()
        this.totalItems = this.importedCreators.totalInfluencers;
        if (this.pagination.itemsPerPage === -1) {
          this.pagination.itemsPerPage = 20;
        }
        this.shouldCallApi = true;
      }
      this.loading = false;
    },
    beautifyData() {
      this.filteredItems = Object.assign([], this.importedCreators.influencers)
      this.filteredItems = this.filteredItems.map(f => {
        f.genre = this.getGenreArr(f.genre)
        let matchTag = this.allAssignedTags.find(a => {
          if (a.handle === f.instagram && a.platform === 'instagram') return true
          if (a.handle === f.tiktok && a.platform === 'tiktok') return true
          if (a.handle === f.youtube && a.platform === 'youtube') return true
          if (a.handle === f.xiaohongshu && a.platform === 'xhs') return true
          return false
        })
        if (matchTag) {
          f.tags = matchTag.names
          f.tagStr = f.tags.join(', ')
        }
        f.emailEdit = false
        return f
      })
    },
    async setRemarks() {
      for (let item of this.filteredItems) {
        let handle = ''
        if (item.instagram) {
          handle = item.instagram
        }
        if (item.tiktok) {
          handle = item.tiktok
        }
        if (item.youtube) {
          handle = item.youtube
        }
        if (item.xiaohongshu) {
          handle = item.xiaohongshu
        }
        item.ccRemarksCount = 0
        let resPairing = await pairingAPIs.getPairingsByHandle(handle)
        if (resPairing) {
          for (let resp of resPairing) {
            if (resp.additional_details) {
              if (resp.additional_details.remarks) {
                item.ccRemarksCount += 1
              }
            }
          }
        }
      }
    },
    setGenreFilters() {
      this.infCategories = []
      this.importedCreatorGenres.map(item => {
        if (item.name) {
          this.infCategories.push(item.name)
        }
      })
    },
    setImportedCreatorCols() {
      this.headers = []
      this.importedCreatorCols.map(ipt => {
        this.headers.push({
          text: ipt,
          value: ipt,
          align: 'start'
        })
      })
      this.headers.push({
        text: 'createdAt',
        value: 'createdAt',
        align: 'start'
      }, {
        text: 'actions',
        value: 'actions',
        align: 'start'
      })
      // this.importedCreatorGenres.map(item => {
      //   if (item.name) {
      //     this.infCategories.push(item.name)
      //   }
      // })
    },
    async setAccountValidity() {
      if (!this.filters.validityCheck) {
        return
      }
      this.importLoading = true
      for (let influencer of this.filteredItems) {
        if (influencer.instagram) {
          // link check
          try {
            let igResp = null
            igResp = await fetch('http://instagram.com/' + influencer.instagram)
            if (igResp) {
              influencer.igCheck = true
            }
          } catch (error) {
            influencer.igCheck = false
            console.log(error)
          }
        }
        if (influencer.tiktok) {
          // link check
          try {
            let tiktokResp = await fetch('https://www.tiktok.com/@' + influencer.tiktok)
            if (tiktokResp) {
              influencer.tiktokCheck = true
            }
          } catch (error) {
            influencer.tiktokCheck = false
            console.log(error)
          }
        }
        if (influencer.youtube) {
          // link check
          try {
            let youtubeResp = await fetch('https://www.youtube.com/@' + influencer.tiktok)
            if (youtubeResp) {
              influencer.youtubeCheck = true
            }
          } catch (error) {
            influencer.youtubeCheck = false
            console.log(error)
          }
        }
      }
      this.importLoading = false
      this.filters.validityCheck = false
      this.filteredItems = Object.assign([], this.filteredItems)
    },
    removeCategory(item) {
      this.filters.infCategory.splice(this.filters.infCategory.indexOf(item), 1);
      this.filters.infCategory = [...this.filters.infCategory];
    },
    remove(item) {
      this.filters.country.splice(this.filters.country.indexOf(item), 1);
      this.filters.country = [...this.filters.country];
      this.paginate()
    },
    removeTravelCountry(item) {
      this.filters.travelCountry.splice(this.filters.travelCountry.indexOf(item), 1);
      this.filters.travelCountry = [...this.filters.travelCountry];
      this.paginate()
    },
    getTypeText(type) {
      if (type === 'agency') return 'Agency'
      if (type === 'direct') return 'Direct'
      return ''
    },
    showReport(item, platformParam = null) {
      this.importLoading = true
      let availInf = this.getInfHandleByPlatform(item, platformParam)
      let infID = availInf.infID
      let platform = availInf.platform
      let handle = availInf.handle
      influencerAPIs
        .fetchLocalAudienceReport(infID, null, handle, platform)
        .then(resp => {
          this.audienceDataReport = resp.data;
          this.viewAudienceDataDialog = true;
          this.importLoading = false
        })
        .catch(err => {
          console.error(err);
        });
    },
    showLoadingEvent(status) {
      this.importLoading = status
    },
    getIMSInfToView(item) {
      let availInf = this.getInfHandlePlatform(item)
      let infID = availInf.infID
      window.open(`/influencers?id=${infID}`, '_blank')
    },
    getAllInfToView(item) {
      let handle = ''
      let platform = ''
      if (item.instagram) {
        handle = item.instagram
        platform = 'instagram'
      } else if (item.tiktok) {
        handle = item.tiktok
        platform = 'tiktok'
      } else if (item.youtube) {
        handle = item.youtube
        platform = 'youtube'
      }
      window.open(`/all-influencers?handle=${handle}&platform=${platform}`, '_blank')
    },
    openColSettingsModal() {
      this.tableColsItems = []
      let tmpStatuses = {}
      let dynamicHeaders = this.headers
      if (Object.keys(this.colStatuses).length) {
        tmpStatuses = Object.assign({}, this.colStatuses)
      } else {
        dynamicHeaders.map(header => {
          tmpStatuses[header.value] = true
        })
      }
      // eslint-disable-next-line
      for (const [key, value] of Object.entries(dynamicHeaders)) {
        if (value.text) {
          this.tableColsItems.push({
            title: value.text,
            key: value.value,
            value: tmpStatuses[value.value] ? tmpStatuses[value.value] : false
          })
        }
      }
      this.colSettingsDialog = true
    },
    async updateTableCols(items) {
      let columns = this.colStatuses
      items.map(item => {
        columns[item.key] = item.value
      })
      let tableSettings = {}
      if (this.userProfile.tableSettings) {
        tableSettings = this.userProfile.tableSettings
      }
      tableSettings.searchCreatorsCols = Object.assign({}, columns)
      let updateData = {
        id: this.currentUserID,
        tableSettings
      }
      await userAPIs.editUser(updateData)
      this.colStatuses = Object.assign({}, columns)
      this.colSettingsDialog = false
    },
    getInfHandlePlatform(item) {
      let infID = item.influencerID || null
      let platform = null
      let handle = null
      if (item.instagram) {
        handle = item.instagram
        platform = 'instagram'
      } else if (item.tiktok) {
        handle = item.tiktok
        platform = 'tiktok'
      } else if (item.youtube) {
        infID = item.youtube
        handle = item.youtube
        platform = 'youtube'
      }
      return {
        infID,
        platform,
        handle
      }
    },
    getInfHandleByPlatform(item, platformParam = null) {
      let infID = item.influencerID || null
      let platform = null
      let handle = null
      if (platformParam === 'instagram') {
        handle = item.instagram
        platform = 'instagram'
      } else if (platformParam === 'tiktok') {
        handle = item.tiktok
        platform = 'tiktok'
      } else if (platformParam === 'youtube') {
        handle = item.youtube
        platform = 'youtube'
      }
      return {
        infID,
        platform,
        handle
      }
    },
    async getUserProfile() {
      let resp = await userAPIs.getUser(this.currentUserID)
      if (resp && resp.id) {
        this.userProfile = resp
      }
    },
    downloadSampleUpload() {
      var link = document.createElement('a');
      // If you don't know the name or want to use
      // the webserver default set name = ''
      link.setAttribute('download', 'sample_creator_bulkupload');
      link.href = '/static/sample/sample_creator_bulkupload.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
    },
    async igHandleBulkUpload(e) {
      const uploadedFiles = Object.keys(e.target.files).map(i => e.target.files[i]);
      if (!uploadedFiles || uploadedFiles.length === 0) {
        this.$swal({
          type: 'info',
          text: 'No file uploaded'
        });
        return;
      }
      let handles = await new Promise((resolve, reject) => {
        var fr = new FileReader();
        fr.onload = (e) => {
          const tmp = utils.func.parserToJson(e.target.result);
          resolve(tmp)
        };
        fr.readAsArrayBuffer(e.target.files[0]);
        fr.onerror = reject;
      });
      // let handleItems = []
      if (!handles.length) return
      this.uploadBulkHandle(handles[0])
      // for (const record of handles[0]) {
      //   let preferredCampaigns = []
      //   if (record['Preferred Campaigns']) {
      //     preferredCampaigns = record['Preferred Campaigns'].split(',')
      //   }
      //   preferredCampaigns = preferredCampaigns.map(p => p.trim())
      //   handleItems.push({
      //     instagram: record['Instagram'],
      //     tiktok: record['TikTok'],
      //     youtube: record['Youtube'],
      //     name: record['Name'],
      //     followers: record['Followers'],
      //     gender: record['Gender'],
      //     country: record.Country ? record.Country.trim() : '',
      //     type: record['Type'] ? (record['Type'].trim()).toLowerCase() : '',
      //     agencyName: record['Agency Name'] || '',
      //     preferredCampaigns,
      //     aboutYou: record['About You'] || '',
      //     genre: record['Genre'] || '',
      //     story: record['story'] || '',
      //     post: record['post'] || '',
      //     tiktokCount: record['Tiktok'] || '',
      //     carousel: record['carousel'] || '',
      //     sixtyReel: record['60s Reel'] || '',
      //     oneEightyReel: record['180s Reel'] || '',
      //     livestreamIntegration: record['Livestream integration'] || '',
      //     livestreamEpisode: record['livestream episode buy'] || '',
      //     dedicatedVideo: record['dedicated video post 5-10 minutes'] || '',
      //     youtubeIntegration: record['youtube video integration'] || '',
      //     youtubeEpisode: record['youtube episode buy'] || '',
      //     loadingFee: record['Loading Fee'] || '',
      //     eventAppearance: record['Event Apperance'] || '',
      //     digitalUsage: record['Digital Usage Rights'] || '',
      //     photoShoot: record['Photo Shoot'] || '',
      //     audienceData: record['Audience Data'] || '',
      //     remarks: record['Remarks'] || ''
      //   })
      // }
      // this.uploadBulkHandle(handleItems)
    },
    uploadBulkHandle(handleItems) { // handleItems : array
      this.importLoading = true
      if (handleItems.length === 0) {
        this.$swal({
          title: 'Warning!',
          icon: 'warning',
          text: 'No Influencer Handle Found'
        });
        return;
      }
      // let formData = new FormData()
      // formData.append('handles', JSON.stringify(handleItems))
      let data = Object.assign({}, {
        handleItems
      })
      influencerAPIs
      .importInfluencers(data)
      .then(resp => {
        this.importLoading = false
        // if (resp.failedInfluencers !== undefined && resp.failedInfluencers.length > 0) {
        //   this.showFailedImportAlert(resp.failedInfluencers)
        //   return false
        // }
        let success = resp.success
        let fails = resp.failed
        let htmlTxt = `Successfully Imported.`
        if (success) {
          htmlTxt += `<br>Success: ${success}`
        }
        if (Array.isArray(fails) && fails.length > 0) {
          htmlTxt += `<br>Failed Rows at: ${fails.join(', ')}`
        }
        this.$swal({
          title: 'Success',
          icon: 'info',
          html: htmlTxt
        });
        this.paginate(this.currentPage)
      })
      .catch(err => {
        this.importLoading = false
        console.error(err);
        this.$swal({
          title: 'Warning!',
          icon: 'warning',
          showCancelButton: false,
          html: `Failed to Import Influencers`
        });
      });
      this.$refs.igBulkHandle.value = null;
    },
    deleteInf(item) {
      this.$swal({
        title: 'Confirm',
        text: 'Are you sure to delete?',
        confirmButtonText: 'Confirm',
        type: 'info',
        showCancelButton: true,
        showCloseButton: true
      }).then(async (result) => {
        if (result.value) {
          this.importLoading = true
          let payload = {
            items: [{
              id: item._id
            }]
          }
          await this.$store.dispatch('influencer/deleteImportedCreator', payload)
          if (this.importedCreators) {
            this.importLoading = false
            this.$swal({
              title: 'Successfully Deleted!',
              type: 'success'
            });
            this.paginate()
          }
        }
      })
    },
    deleteMultipleInf() {
      this.$swal({
        title: 'Confirm',
        text: 'Are you sure to delete?',
        confirmButtonText: 'Confirm',
        type: 'info',
        showCancelButton: true,
        showCloseButton: true
      }).then(async (result) => {
        if (result.value) {
          this.importLoading = true
          let payload = {
            items: []
          }
          this.selectedLists.map(s => {
            payload.items.push({
              id: s._id
            })
          })
          await this.$store.dispatch('influencer/deleteImportedCreator', payload)
          if (this.importedCreators) {
            this.importLoading = false
            this.$swal({
              title: 'Successfully Deleted!',
              type: 'success'
            });
            this.selectedLists = []
            this.paginate()
          }
        }
      })
    },
    async fetchIQData(item, platform) {
      // fetch from IQ Data by platform
      this.importLoading = true
      let payload = {
        id: item._id,
        platform
      }
      await this.$store.dispatch('influencer/fetchIQDataImportedCreator', payload)
      this.importLoading = false
      if (this.importedCreatorDtl) {
        let updateItemIdx = this.filteredItems.findIndex(fi => fi._id === item._id)
        if (updateItemIdx !== -1) {
          this.filteredItems[updateItemIdx] = Object.assign({}, this.importedCreatorDtl)
          this.filteredItems = Object.assign([], this.filteredItems)
        }
        this.$swal({
          title: 'Successfully Fetched!',
          type: 'success'
        });
      } else {
        this.$swal({
          title: this.socialDataErrMsg,
          type: 'warning'
        });
      }
    },
    selectAllLists($event) {
      if (!$event.value) {
        this.selectedLists = [];
        return;
      }
      this.selectedLists = Object.assign([], this.filteredItems);
    },
    async getImportedCreatorGenres() {
      await this.$store.dispatch('influencer/getImportedCreatorGenres')
      this.setGenreFilters()
    },
    async getImportedCreatorsCols() {
      await this.$store.dispatch('influencer/getImportedCreatorsCols')
      // this.setImportedCreatorCols()
    },
    async getAllCampaigns() {
      await this.$store.dispatch('campaign/getCampaigns', {
        filters: {
          defaultAll: true
        }
      })
      this.allCampaigns = Object.assign([], this.campaigns)
      this.allCampaigns = this.allCampaigns.map(element => {
        return {
          name: element.name,
          fullName: element.ioNumbers ? `${element.ioNumbers} - ${element.name}` : element.name,
          id: element.id
        };
      });
    },
    async openCampaignListsModal(item, isRemark = false) {
      this.importLoading = true
      this.isCcRemarks = false
      let pairings = []
      let campaignIDs = []
      let handle = ''
      if (item.instagram) {
        handle = item.instagram
      }
      if (item.tiktok) {
        handle = item.tiktok
      }
      if (item.youtube) {
        handle = item.youtube
      }
      if (item.xiaohongshu) {
        handle = item.xiaohongshu
      }
      let resp = await pairingAPIs.getPairingsByHandle(handle)
      if (resp) {
        pairings.push(...resp)
        for (let pairing of pairings) {
          campaignIDs.push(pairing.campaignID)
        }
      } else {
        if (item.distinctCampaigns) {
          campaignIDs = item.distinctCampaigns
        }
      }
      this.viewHandle = handle
      this.viewCampaigns = this.allCampaigns.filter(c => campaignIDs.includes(c.id))
      // match campaign with pairing date to retrieve deliverables, payout amount
      this.viewCampaigns = this.viewCampaigns.map(v => {
        let matchPairing = pairings.find(p => {
          if (p.influencer_details) {
            if (p.influencer_details.instagramHandle) {
              if (p.campaignID === v.id && p.influencer_details.instagramHandle === handle) {
                return true
              }
            }
          }
          return false
        })
        if (matchPairing) {
          if (matchPairing.additional_details) {
            v.ccRemarks = matchPairing.additional_details.remarks || ''
          }
          v.deliverables = matchPairing.deliverables_details
          v.payout = matchPairing.payout_details.amount
          v.pairingDate = matchPairing.createdAt ? moment(matchPairing.createdAt).format('DD-MM-YYYY') : ''
        }
        return v
      })
      this.emptyCampaigns = Number(item.campaignsCount) - this.viewCampaigns.length
      this.setShowCampaignVerticals()
      this.viewCampaignListsDialog = true
      this.importLoading = false
      if (isRemark) {
        this.viewCampaigns = this.viewCampaigns.filter(v => v.ccRemarks)
        this.isCcRemarks = true
      }
    },
    setShowCampaignVerticals() {
      this.viewCampaigns = this.viewCampaigns.map(v => {
        if (v.vertical && v.vertical.length > 0) {
          v.showVerticals = v.vertical.map(vt => vt.name).join(', ')
        } else {
          v.showVerticals = '-'
        }
        v.campaignStatusDisplay = this.getStageName(v.status);
        return v
      })
    },
    getStageName(status) {
      if (status === this.campaignStatuses.DRAFT || status === this.campaignStatuses.PENDING_CLIENT_APPROVAL) {
        return this.campaignStage.ACTIVATING
      } else if (status === this.campaignStatuses.PAIRING_BRIEF || status === this.campaignStatuses.PAIRING_INFLUENCER || status === this.campaignStatuses.RATE_CARD) {
        return this.campaignStage.PAIRING
      } else if (status === this.campaignStatuses.ONGOING) {
        return this.campaignStage.COCREATING
      } else {
        return this.campaignStage.COMPLETE
      }
    },
    formatDesc(text) {
      if (text) {
        return text.replaceAll('\n', '</br>')
      }
      return text
    },
    async getAllTags() {
      await this.$store.dispatch('influencer/getAllTags')
    },
    async getAllAssignedTags() {
      await this.$store.dispatch('influencer/getAllAssignedTags')
    },
    showAddTagDialog(post) {
      this.addTagDialog = true
      if (post.instagram) {
        post.userHandle = post.instagram
        post.platform = 'instagram'
      } else if (post.tiktok) {
        post.userHandle = post.tiktok
        post.platform = 'tiktok'
      } else if (post.youtube) {
        post.userHandle = post.youtube
        post.platform = 'youtube'
      } else if (post.xiaohongshu) {
        post.userHandle = post.xiaohongshu
        post.platform = 'xhs'
      }
      this.editTagItem = Object.assign({}, post)
    },
    saveTagProcess(tags) {
      let matchIdx = this.filteredItems.findIndex(f => f.id === this.editTagItem.id)
      if (matchIdx !== -1) {
        this.filteredItems[matchIdx].tags = tags
      }
    },
    getRateCardDataToView(item) {
      if (!item) return
      if (!item.preferentialRates) { item.preferentialRates = {} }
      if (!item.ordinaryRates) {
        item.ordinaryRates = {
          igStory: item.igStory || 0,
          igStoryCurrency: 'SGD',
          igPost: item.igPost || 0,
          igPostCurrency: 'SGD',
          igCarousel: item.igCarousel || 0,
          igCarouselCurrency: 'SGD',
          igVideo: item.igVideo || 0,
          igVideoCurrency: 'SGD',
          igReel: item.igReel || 0,
          igReelCurrency: 'SGD',
          igTv: item.igTv || 0,
          igTvCurrency: 'SGD',
          loadingFee: item.loadingFee || 0,
          loadingFeeCurrency: 'SGD',
          eventAppearance: item.eventAppearance || 0,
          eventAppearanceCurrency: 'SGD',
          tiktok: item.tiktok || 0,
          tiktokCurrency: 'SGD'
        }
      }
      if (!item.keywords) item.keywords = []
      if (!item.mainCategories) item.mainCategories = []
      if (!item.subCategories) item.subCategories = []
      this.rateCardViewData = item
      this.rateCardViewDialog = true
    },
    getGenreArr(genres) {
      if (typeof genres === 'string') {
        genres = genres.split(',')
      }
      return genres
    },
    reload() {
      this.filters = {
        keyword: '',
        platform: '',
        tags: [],
        tagHandles: [],
        gender: '',
        tier: '',
        dateOfBirth: '',
        vertical: '',
        campaignType: '',
        ageGroup: '',
        age: '',
        country: [],
        infCategory: [],
        imsOnly: false,
        rateCardOnly: false,
        pairedCreatorOnly: false,
        travelFormOnly: false,
        travelCountry: [],
        travelDate: '',
        validityCheck: false,
        campaign: '',
        contentType: '',
        rateCurrency: '',
        rate: {from: '', to: ''},
      }
      this.paginate()
    },
    getInfluencerDataToView(influencerID, infItem) {
      influencerAPIs
        .getInfluencer(influencerID)
        .then(response => {
          let influencerUserData = this.users.find(u => u.influencerID === response.id)
          if (influencerUserData) {
            response.userID = influencerUserData.id || ''
            response.firstName = influencerUserData.firstName || ''
            response.lastName = influencerUserData.lastName || ''
          }
          if (infItem) {
            if (infItem.travelForm) {
              response.travelForm = infItem.travelForm
              delete response.travelForm._id
              delete response.travelForm.influencerID
            }
          }
          this.viewInfluencerData = response;
          this.viewInfluencerDialog = true;
        })
        .catch(error => {
          // Request failed.
          console.log('error', error);
          if (error.response && error.response.status === 404) {
            this.$swal({
              title: 'Warning!',
              icon: 'warning',
              text: 'IMS Influencer Not Found'
            });
          }
        });
    },
    getTravelDataToView(item) {
      this.viewTravelData = Object.assign({}, item.travelForm)
      this.viewTravelDialog = true
    },
    async getUsers() {
      // Get all Influencers User Data by role
      let response = await userAPIs.getAllUsersByRole(config.userRoles.INFLUENCER)
      if (response) {
        this.users = response.users
      }
    },
    async exportExcel() {
      // Export excel search creaters
      this.importLoading = true
      this.payload.export = true
      influencerAPIs
        .downloadSearchCreatorRpt(this.payload)
        .then(() => {
          this.importLoading = false
        })
        .catch(error => {
          this.importLoading = false
          console.log('error in downloading search creator report', error);
        });
    },
    async getCampaignVerticals() {
      // Get all campaign verticals and unshift for filter
      let vResp = await campaignAPIs.getAllCampaignVerticals(false)
      this.campaignVerticals = vResp.data
      // this.campaignVerticals.unshift({id: '', name: 'All Verticals'})
    },
    changeImportedCreatorEdit(colName, item, status) {
      let matchItemIdx = this.filteredItems.findIndex(f => f._id === item._id)
      if (matchItemIdx !== -1) {
        this.filteredItems[matchItemIdx][colName] = status
        this.filteredItems = utils.func.refreshJSON(this.filteredItems)
      }
    },
    async updateImportedCreator(item, field) {
      this.importLoading = true
      let updateIC = [{
        id: item._id,
        email: item[field]
      }]
      await this.$store.dispatch('influencer/updateImportedCreatorByIds', updateIC)
      if (this.savedImportedCreator) {
        let matchItemIdx = this.filteredItems.findIndex(f => f._id === item._id)
        if (matchItemIdx !== -1) {
          this.filteredItems[matchItemIdx][field] = item[field]
          this.filteredItems = utils.func.refreshJSON(this.filteredItems)
          if (this.filteredItems[matchItemIdx][`${field}Edit`]) {
            this.filteredItems[matchItemIdx][`${field}Edit`] = false
          }
        }
      }
      this.importLoading = false
    },
    cancelDOB() {
      this.dateOfBirthCal = false
    },
    okDOB() {
      this.dateOfBirthCal = false
      this.paginate()
    },
    cancelTravelDate() {
      this.travelDateCal = false
    },
    okTravelDate() {
      this.travelDateCal = false
      this.paginate()
    },
    viewStatsAnalysis(item) {
      let handle = ''
      if (item.instagram) {
        handle = item.instagram
      } else {
        if (item.tiktok) {
          handle = item.tiktok
        }
        if (item.youtube) {
          handle = item.youtube
        }
      }
      if (!handle) {
        this.$swal({
          type: 'info',
          text: 'Handle Not Found.'
        });
        return
      }
      this.viewHandle = handle
      this.viewStatsDialog = true
    }
  },
  async mounted() {
    // set tier all
    this.infTiers.unshift({
      text: 'All Tiers',
      value: ''
    })
    // Retrieve countries manually
    this.countries = countries
    this.countries = this.countries.map(c => c.name)
    this.getAllTags()
    this.getAllAssignedTags()
    this.getImportedCreatorGenres()
    this.getImportedCreatorsCols()
    this.paginate()
    this.getAllCampaigns()
    await this.getUserProfile()
    this.getUsers()
    this.getCampaignVerticals()
  }
}
</script>
<style scoped>
  .btn-ims {
    top: 6px;
    height: 35px!important;
    margin-left: 5px;
  }
</style>
<style>
  .creator-search-page {
    color: #20263a;
  }

  .v-tooltip__content {
    z-index: 3000 !important;
  }

  .creator-search-panel,
  .creator-results-panel {
    border: 1px solid #e4e7ef;
    border-radius: 16px;
    background: #fff;
    box-shadow: 0 10px 32px rgba(31, 39, 70, 0.055);
  }

  .creator-search-panel {
    margin-bottom: 10px;
    padding: 10px 12px;
  }

  .creator-search-toolbar,
  .creator-results-toolbar,
  .creator-advanced-filters__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
  }

  .creator-search-toolbar {
    margin-bottom: 16px;
  }

  .creator-search-toolbar__title,
  .creator-advanced-filters__header > div {
    display: flex;
    flex-direction: column;
  }

  .creator-search-toolbar__title > span,
  .creator-advanced-filters__header span {
    color: #282f43;
    font-size: 15px;
    font-weight: 750;
  }

  .creator-search-toolbar__title small,
  .creator-advanced-filters__header small {
    margin-top: 2px;
    color: #9298a9;
    font-size: 12px;
  }

  .creator-search-toolbar__actions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
  }

  .creator-action-btn {
    margin: 0 !important;
    border-color: #dde1ea !important;
    border-radius: 9px !important;
    color: #596176 !important;
    font-weight: 700 !important;
    letter-spacing: 0 !important;
    text-transform: none !important;
  }

  .creator-action-btn--primary {
    color: #fff !important;
  }

  .creator-search-primary {
    display: flex;
    align-items: stretch;
    gap: 8px;
  }

  .creator-search-input .v-input__slot {
    min-height: 40px !important;
    border-radius: 9px !important;
    background: #fafbfe !important;
  }

  .creator-search-input fieldset {
    border-color: #dfe3ec !important;
  }

  .creator-search-submit {
    min-width: 104px !important;
    height: 40px !important;
    border-radius: 9px !important;
    box-shadow: 0 8px 18px rgba(67, 78, 151, 0.2) !important;
    font-weight: 750 !important;
    letter-spacing: 0 !important;
    text-transform: none !important;
  }

  .creator-filters-toggle,
  .creator-actions-toggle {
    height: 40px !important;
    border-color: #dde1ea !important;
    border-radius: 9px !important;
    color: #555e73 !important;
    font-weight: 700 !important;
    letter-spacing: 0 !important;
    text-transform: none !important;
  }

  .creator-filter-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 19px;
    height: 19px;
    margin-left: 7px;
    padding: 0 5px;
    border-radius: 999px;
    background: #4c5bd4;
    color: #fff;
    font-size: 10px;
    font-weight: 800;
  }

  .creator-actions-menu .v-list-item__icon {
    min-width: 28px;
    margin: 10px 4px 10px 0;
  }

  .creator-filters-accordion {
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid #eff1f5;
  }

  .creator-quick-filters {
    display: flex;
    align-items: center;
    gap: 16px;
    margin: 0 0 10px;
    padding: 0;
  }

  .creator-quick-filters__label {
    flex: 0 0 auto;
    color: #8b92a5;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .creator-quick-filters__options {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
  }

  .creator-filter-toggle {
    padding: 4px 12px;
    border: 1px solid #e3e6ee;
    border-radius: 999px;
    background: #fafbfc;
  }

  .creator-filter-toggle .v-input--checkbox {
    padding: 0 !important;
  }

  .creator-filter-toggle .v-input--selection-controls__input {
    margin-right: 4px !important;
  }

  .creator-filter-toggle .v-label {
    color: #565e72;
    font-size: 12px;
    font-weight: 650;
  }

  .creator-platform-option {
    display: inline-flex;
    align-items: center;
    min-width: 0;
    gap: 9px;
  }

  .creator-platform-option--selected {
    gap: 7px;
  }

  .creator-platform-option__icon {
    flex: 0 0 20px;
    width: 20px;
  }

  .creator-platform-option__image {
    display: block;
    flex: 0 0 20px;
    width: 20px;
    height: 20px;
    object-fit: contain;
  }

  .creator-advanced-filters {
    margin-top: 8px;
    padding: 12px 0 0;
    border: 0;
    border-top: 1px solid #eff1f5;
    border-radius: 0;
    box-shadow: none;
  }

  .creator-advanced-filters__header {
    margin-bottom: 8px;
    padding-bottom: 0;
    border-bottom: 0;
  }

  .creator-advanced-filters > .row {
    display: grid !important;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 10px;
    margin: 0 !important;
  }

  .creator-advanced-filters > .row > [class*="col-"] {
    width: auto !important;
    max-width: none !important;
    padding: 5px 0 !important;
  }

  .creator-advanced-filters .v-input__slot {
    border-radius: 9px !important;
    background: #fbfcfe !important;
  }

  .creator-advanced-filters fieldset {
    border-color: #e1e4ec !important;
  }

  .creator-rate-filter .v-input__slot {
    cursor: pointer;
  }

  .creator-rate-filter input {
    cursor: pointer;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .creator-rate-filter .v-input__icon--clear .v-icon {
    color: #3F3FFF !important;
  }

  .creator-rate-popover-input .v-input__icon--clear .v-icon,
  .creator-rate-popover-input .v-input__icon--append .v-icon {
    color: #3F3FFF !important;
    caret-color: #3F3FFF !important;
  }

  .creator-rate-popover-input.v-input--is-focused .v-icon,
  .creator-rate-popover-input .v-icon.primary--text {
    color: #3F3FFF !important;
    caret-color: #3F3FFF !important;
  }

  .creator-rate-popover-input.v-input--is-focused fieldset {
    border-color: #3F3FFF !important;
  }

  .creator-rate-filter-actions {
    display: flex;
    justify-content: space-between;
    width: 100%;
  }

  .creator-content-type-option {
    display: inline-flex;
    align-items: center;
    min-width: 0;
    gap: 8px;
  }

  .creator-content-type-option--selected {
    gap: 6px;
  }

  .creator-content-type-option .v-icon {
    color: var(--media-icon-color) !important;
    caret-color: var(--media-icon-color) !important;
  }

  .creator-content-type-option img {
    display: block;
    flex: 0 0 18px;
    width: 18px;
    height: 18px;
    object-fit: contain;
  }

  .creator-results-panel {
    overflow: hidden;
    padding: 0;
  }

  .creator-results-toolbar {
    min-height: 48px;
    padding: 6px 12px;
    border-bottom: 1px solid #eceef3;
    background: #fff;
  }

  .creator-results-toolbar__actions {
    display: flex;
    align-items: center;
  }

  .creator-results-count {
    color: #7b8295;
    font-size: 12px;
    font-weight: 650;
  }

  .creator-results-count strong {
    margin-right: 3px;
    color: #252c40;
    font-size: 14px;
  }

  .creator-results-toolbar__actions .v-btn {
    border-radius: 8px !important;
  }

  #search-creator-table {
    border-radius: 0 !important;
    box-shadow: none !important;
  }

  #search-creator-table .v-data-table-header {
    background: #f8f9fc;
  }

  #search-creator-table .v-data-table-header th {
    color: #6f768a;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.045em;
    text-transform: uppercase;
  }

  #search-creator-table tbody tr {
    transition: background 0.16s ease;
  }

  #search-creator-table tbody tr:hover {
    background: #fffaf2 !important;
  }

  #search-creator-table tbody td {
    border-color: #f0f1f5 !important;
    color: #3e4559;
    font-size: 13px;
  }

  #search-creator-table tbody td .remark-text {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100px;
    max-height: 100px;
  }

  @media (max-width: 1100px) {
    .creator-advanced-filters > .row {
      /* Keep the three filter groups on three lines instead of expanding
         into five rows on medium desktop screens. */
      grid-template-columns: repeat(5, minmax(0, 1fr));
    }
  }

  @media (max-width: 760px) {
    .creator-search-toolbar,
    .creator-quick-filters {
      align-items: flex-start;
      flex-direction: column;
    }

    .creator-search-toolbar__actions,
    .creator-quick-filters__options {
      width: 100%;
    }

    .creator-search-primary {
      flex-direction: column;
    }

    .creator-search-submit {
      width: 100%;
    }

    .creator-filters-toggle,
    .creator-actions-toggle {
      width: 100%;
    }

    .creator-advanced-filters > .row {
      grid-template-columns: 1fr;
    }
  }
</style>



// WEBPACK FOOTER //
// DataTable.vue?eafe4b8a